import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Eye, EyeOff, Save, CreditCard, CheckCircle2, AlertCircle, Wallet, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const AVAILABLE_GATEWAYS = [
  {
    id: 'mercadopago',
    name: 'Mercado Pago',
    logo: '💳',
    color: 'from-blue-500 to-cyan-500',
    description: 'PIX',
    tokenPlaceholder: 'APP_USR-xxxx-xxxx-xxxx',
    tokenLabel: 'Access Token',
    helpUrl: 'https://www.mercadopago.com.br/developers/panel/app',
    helpText: 'Como obter o token →',
    minValue: null
  },
  {
    id: 'fastdepix',
    name: 'FastDePix',
    logo: '⚡',
    color: 'from-purple-500 to-pink-500',
    description: 'PIX instantâneo',
    tokenPlaceholder: 'fdpx_live_xxxx...',
    tokenLabel: 'API Key',
    helpUrl: 'https://fastdepix.space',
    helpText: 'Obter API Key →',
    minValue: 10,
    minValueWarning: '⚠️ Valor mínimo: R$10,00'
  },
  {
    id: 'rocketpay',
    name: 'RocketPay',
    logo: '🚀',
    color: 'from-red-500 to-orange-500',
    description: 'PIX, USDT e conversão',
    tokenPlaceholder: 'client_id:client_secret',
    tokenLabel: 'Client ID : Client Secret',
    helpUrl: 'https://rocketpaydigital.com',
    helpText: 'Obter credenciais →',
    minValue: null,
    helpExtra: 'Cole no formato: client_id:client_secret'
  },
  {
    id: 'ativopay',
    name: 'AtivoPay',
    logo: '💎',
    color: 'from-indigo-500 to-violet-500',
    description: 'PIX com API robusta',
    tokenPlaceholder: 'Sua API Key do AtivoPay',
    tokenLabel: 'API Key (x-api-key)',
    helpUrl: 'https://ativob2b.com.br',
    helpText: 'Obter API Key →',
    minValue: null
  },
  {
    id: 'asaas',
    name: 'Asaas',
    logo: '🏦',
    color: 'from-blue-600 to-indigo-600',
    description: 'PIX instantâneo',
    tokenPlaceholder: '$aact_xxxx... (chave de API do Asaas)',
    tokenLabel: 'Chave de API (access_token)',
    helpUrl: 'https://www.asaas.com/customerApiAccessToken/index',
    helpText: 'Obter chave de API →',
    minValue: null,
    helpExtra: 'No Asaas: Integrações → Chaves de API → Gerar chave. Use a chave de produção para receber de verdade.',
    notice: 'Cadastre uma chave PIX na sua conta Asaas (Configurações → Chaves PIX). É possível gerar cobranças sem ela, mas o Asaas vai descontinuar esse modo — com a chave cadastrada, o PIX funciona sempre.'
  },
  {
    id: 'infinitepay',
    name: 'InfinitePay',
    logo: '♾️',
    color: 'from-violet-600 to-purple-500',
    description: 'PIX + Cartão até 12x',
    tokenPlaceholder: 'sua_infinite_tag (sem o $)',
    tokenLabel: 'InfiniteTag (handle)',
    helpUrl: 'https://app.infinitepay.io/external-checkout',
    helpText: 'Abrir o Checkout no app InfinitePay →',
    minValue: null,
    helpExtra: 'É só o seu nome de usuário (InfiniteTag) — não precisa de token! Ex: se aparece $minhaloja no app, digite minhaloja (sem o $).',
    warning: '⚠️ Atenção: o InfinitePay NÃO funciona no aplicativo (Play Store). Como o pagamento abre a página da InfinitePay (fora do sistema), ele só funciona no link de pagamento pelo navegador. Para receber pelo app, use MercadoPago ou Asaas (que geram o PIX dentro do próprio sistema/app).',
    notice: 'COMO ATIVAR (1x só): 1) Acesse app.infinitepay.io/external-checkout  2) Clique em Checkout → Configurações → Habilitar Checkout Integrado.  3) Copie sua InfiniteTag (canto superior esquerdo do app, sem o $) e cole aqui.  Depois ligue "Aceitar cartão" abaixo. O cliente escolhe PIX (taxa zero) ou cartão até 12x na página segura da InfinitePay.'
  },
  {
    id: 'pagbank',
    name: 'PagBank',
    logo: '🅿️',
    color: 'from-emerald-600 to-green-500',
    description: 'PIX instantâneo',
    tokenPlaceholder: 'Seu token de API do PagBank',
    tokenLabel: 'Token de API (Bearer)',
    helpUrl: 'https://developer.pagbank.com.br/reference/introducao',
    helpText: 'Como obter o token →',
    minValue: null,
    notice: 'Cadastre uma chave PIX na sua conta PagBank. Sem uma chave PIX ativa, o PagBank não gera o QR Code das cobranças.'
  },
  {
    id: 'nexupay',
    name: 'NexuPay',
    logo: null,
    logoIcon: 'wallet',
    color: 'from-emerald-500 to-teal-500',
    description: 'Carteira digital NexuPay — receba pagamentos direto na carteira',
    noToken: true,
    helpText: 'Gerenciar NexuPay →',
    helpUrl: '/nexupay',
    minValue: null
  }
];

export default function GatewaysPage() {
  const { api, user } = useAuthStore();
  const navigate = useNavigate();
  const [gateways, setGateways] = useState({});
  const [loading, setLoading] = useState(true);
  const [showTokens, setShowTokens] = useState({});
  const [saving, setSaving] = useState({});
  const [expandedCard, setExpandedCard] = useState(null);

  useEffect(() => {
    if (user?.id) {
      fetchGateways();
    }
  }, [user?.id]);

  const fetchGateways = async () => {
    try {
      const response = await api.get('/gateways');
      console.log('[GATEWAYS] Dados recebidos:', response.data);
      console.log('[GATEWAYS] User ID:', user?.id);
      const gatewaysMap = {};
      
      // Filtrar apenas os gateways do usuário logado (se tiver user.id)
      const myGateways = user?.id 
        ? response.data.filter(gw => gw.user_id === user.id)
        : response.data;
      
      myGateways.forEach(gw => {
        gatewaysMap[gw.provider] = gw;
      });
      console.log('[GATEWAYS] Map atualizado (filtrado):', gatewaysMap);
      setGateways(gatewaysMap);
    } catch (error) {
      console.error('[GATEWAYS] Erro:', error);
      toast.error('Erro ao carregar gateways');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCard = async (provider, enabled) => {
    setSaving(prev => ({ ...prev, [provider]: true }));
    try {
      await api.put(`/gateways/${provider}/card-active`, { card_enabled: enabled });
      setGateways(prev => {
        const novo = { ...prev };
        // ao ativar, desativa card_active dos outros (só 1 gateway de cartão)
        if (enabled) Object.keys(novo).forEach(k => { if (novo[k]) novo[k] = { ...novo[k], card_active: false }; });
        novo[provider] = { ...novo[provider], card_active: enabled, card_enabled: enabled ? true : novo[provider]?.card_enabled };
        return novo;
      });
      toast.success(enabled ? 'Este gateway agora recebe os pagamentos por cartão!' : 'Cartão desativado neste gateway');
    } catch (e) {
      toast.error('Erro ao alterar cartão');
    } finally {
      setSaving(prev => ({ ...prev, [provider]: false }));
    }
  };

  const handleToggle = async (provider, enabled) => {
    const current = gateways[provider];
    console.log('[TOGGLE] Provider:', provider, 'Enabled:', enabled, 'Current:', current);
    
    // NexuPay não precisa de token — toggle direto
    const gwConfig = AVAILABLE_GATEWAYS.find(g => g.id === provider);
    if (enabled && !gwConfig?.noToken && !current?.access_token_preview) {
      toast.error('Configure o token antes de habilitar');
      return;
    }

    setSaving(prev => ({ ...prev, [provider]: true }));
    
    try {
      const response = await api.put(`/gateways/${provider}/toggle`, { active: enabled });
      console.log('[TOGGLE] Response:', response.data);
      
      if (enabled) {
        toast.success('Gateway ativado! Outros gateways foram desativados automaticamente.');
      } else {
        toast.success('Gateway desabilitado!');
      }
      
      await fetchGateways();
    } catch (error) {
      console.error('[TOGGLE] Error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atualizar gateway');
    } finally {
      setSaving(prev => ({ ...prev, [provider]: false }));
    }
  };

  const handleSaveToken = async (provider, token) => {
    if (!token.trim()) {
      toast.error('Token não pode ser vazio');
      return;
    }

    setSaving(prev => ({ ...prev, [provider]: true }));
    
    try {
      await api.post('/gateways', {
        provider,
        access_token: token,
        active: gateways[provider]?.active || false
      });
      toast.success('Gateway configurado!');
      await fetchGateways();
      setExpandedCard(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar configuração');
    } finally {
      setSaving(prev => ({ ...prev, [provider]: false }));
    }
  };

  const updateLocalToken = (provider, token) => {
    setGateways(prev => ({
      ...prev,
      [provider]: {
        ...prev[provider],
        _localToken: token
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
          <CreditCard className="w-8 h-8 text-primary" />
          Gateways de Pagamento
        </h1>
        <p className="text-muted-foreground">Configure seus gateways para receber pagamentos</p>
      </div>

      {/* Aviso */}
      <div className="flex items-start gap-3 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
        <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-medium text-yellow-500">Apenas um gateway pode estar ativo por vez</p>
          <p className="text-muted-foreground">Ao ativar um gateway, os outros serão desativados automaticamente.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {AVAILABLE_GATEWAYS.map((gateway) => {
          const config = gateways[gateway.id] || {};
          const isEnabled = config.active || false;
          const hasToken = !!config.access_token_preview;
          const localToken = config._localToken || '';
          const isExpanded = expandedCard === gateway.id;
          
          return (
            <Card 
              key={gateway.id} 
              className={`relative overflow-hidden transition-all duration-300 ${
                gateway.comingSoon ? 'opacity-60' : 'hover:shadow-lg cursor-pointer'
              } ${isExpanded ? 'col-span-1 sm:col-span-2' : ''}`}
              onClick={() => !gateway.comingSoon && setExpandedCard(isExpanded ? null : gateway.id)}
            >
              {/* Gradient Header */}
              <div className={`h-2 bg-gradient-to-r ${gateway.color}`} />
              
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {gateway.logoIcon === 'wallet' ? (
                      <div className="w-8 h-8 rounded-full bg-emerald-500/15 flex items-center justify-center">
                        <Wallet className="w-5 h-5 text-emerald-500" />
                      </div>
                    ) : (
                      <span className="text-2xl">{gateway.logo}</span>
                    )}
                    <div>
                      <h3 className="font-bold">{gateway.name}</h3>
                      <p className="text-xs text-muted-foreground">{gateway.description}</p>
                      {gateway.warning && (
                        <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 text-[9px] font-semibold">🚫 Não funciona no app</span>
                      )}
                    </div>
                  </div>
                  
                  {!gateway.comingSoon && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <Switch
                        checked={isEnabled}
                        onCheckedChange={(checked) => handleToggle(gateway.id, checked)}
                        disabled={saving[gateway.id]}
                      />
                    </div>
                  )}
                </div>

                {/* Status Badge */}
                {!gateway.comingSoon && (
                  <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
                    isEnabled 
                      ? 'bg-green-500/15 text-green-500' 
                      : (hasToken || gateway.noToken)
                        ? 'bg-yellow-500/15 text-yellow-500'
                        : 'bg-muted text-muted-foreground'
                  }`}>
                    {isEnabled ? (
                      <>
                        <CheckCircle2 className="w-3 h-3" />
                        Ativo
                      </>
                    ) : (hasToken || gateway.noToken) ? (
                      gateway.noToken ? 'Pronto' : 'Configurado'
                    ) : (
                      'Não configurado'
                    )}
                  </div>
                )}

                {/* Chip de CARTÃO (visível, sem precisar expandir) — só MP/Asaas/InfinitePay */}
                {['mercadopago', 'asaas', 'infinitepay'].includes(gateway.id) && hasToken && (
                  <div onClick={(e) => e.stopPropagation()} className="mt-2">
                    <button
                      onClick={() => handleToggleCard(gateway.id, config.card_active !== true)}
                      disabled={saving[gateway.id]}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                        config.card_active === true
                          ? 'bg-indigo-500/15 text-indigo-400 border-indigo-500/40'
                          : 'bg-muted/40 text-muted-foreground border-border hover:border-indigo-500/40 hover:text-indigo-400'
                      }`}
                    >
                      💳 {config.card_active === true ? 'Recebe cartão ✓' : 'Usar p/ cartão'}
                    </button>
                  </div>
                )}

                {gateway.comingSoon && (
                  <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                    🚀 Em breve
                  </div>
                )}

                {/* Aviso de valor mínimo */}
                {gateway.minValueWarning && (
                  <div className="mt-2 text-xs text-yellow-500 font-medium">
                    {gateway.minValueWarning}
                  </div>
                )}

                {/* Aviso CRÍTICO (ex.: InfinitePay não funciona no app) — só quando expandido */}
                {isExpanded && gateway.warning && (
                  <div className="mt-2 flex items-start gap-2 rounded-lg border border-red-500/40 bg-red-500/10 p-2.5">
                    <span className="text-red-500 text-sm shrink-0">🚫</span>
                    <p className="text-[11px] text-red-600 dark:text-red-400 leading-snug font-medium">{gateway.warning}</p>
                  </div>
                )}
                {/* Aviso específico do gateway (ex.: chave PIX no Asaas) — só quando expandido */}
                {isExpanded && gateway.notice && (
                  <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-2.5">
                    <span className="text-amber-500 text-sm shrink-0">⚠️</span>
                    <p className="text-[11px] text-amber-600 dark:text-amber-400 leading-snug">{gateway.notice}</p>
                  </div>
                )}

                {/* Expanded Content */}
                {isExpanded && !gateway.comingSoon && (
                  <div className="mt-4 pt-4 border-t space-y-3" onClick={(e) => e.stopPropagation()}>
                    {gateway.noToken ? (
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground">
                          Ao ativar o NexuPay, os pagamentos dos seus clientes serao creditados automaticamente na sua carteira digital.
                        </p>
                        <a 
                          href="/nexupay"
                          className="text-xs text-primary hover:underline block font-medium"
                        >
                          {gateway.helpText || 'Gerenciar NexuPay →'}
                        </a>
                      </div>
                    ) : (
                      <>
                        <div>
                          <Label className="text-xs">{gateway.tokenLabel || 'Access Token'}</Label>
                          <div className="flex gap-2 mt-1">
                            <div className="relative flex-1">
                              <Input
                                type={showTokens[gateway.id] ? 'text' : 'password'}
                                value={localToken}
                                onChange={(e) => updateLocalToken(gateway.id, e.target.value)}
                                placeholder={config.access_token_preview || gateway.tokenPlaceholder}
                                className="pr-10 text-sm"
                              />
                              <button
                                type="button"
                                onClick={() => setShowTokens(prev => ({ ...prev, [gateway.id]: !prev[gateway.id] }))}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              >
                                {showTokens[gateway.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                            <Button
                              onClick={() => handleSaveToken(gateway.id, localToken)}
                              disabled={!localToken.trim() || saving[gateway.id]}
                              size="sm"
                            >
                              <Save className="w-4 h-4" />
                            </Button>
                          </div>
                          {config.access_token_preview && (
                            <p className="text-[10px] text-muted-foreground mt-1">
                              Atual: {config.access_token_preview}
                            </p>
                          )}
                        </div>
                        <a 
                          href={gateway.helpUrl} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-xs text-primary hover:underline block"
                        >
                          {gateway.helpText || 'Como obter o token →'}
                        </a>
                        {gateway.helpExtra && (
                          <p className="text-xs text-muted-foreground">{gateway.helpExtra}</p>
                        )}
                      </>
                    )}
                    {/* Toggle de recebimento por cartão (só MP e Asaas) */}
                    {(gateway.id === 'mercadopago' || gateway.id === 'asaas' || gateway.id === 'infinitepay') && (hasToken || isEnabled) && (
                      <label className="flex items-center justify-between rounded-lg bg-muted/40 p-3 cursor-pointer">
                        <div>
                          <span className="text-sm font-medium flex items-center gap-1.5">💳 Receber CARTÃO por este gateway</span>
                          <p className="text-[11px] text-muted-foreground mt-0.5">Os pagamentos com cartão passam por este gateway. Só um gateway pode receber cartão por vez (o PIX pode ficar em outro).</p>
                        </div>
                        <Switch
                          checked={config.card_active === true}
                          onCheckedChange={(v) => handleToggleCard(gateway.id, v)}
                          disabled={saving[gateway.id]}
                        />
                      </label>
                    )}
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* NexuPay Card separado removido - agora está na lista de gateways */}
    </div>
  );
}
